import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useParams } from 'react-router-dom';

export interface PillarPageData {
  monthly_pillar_metrics: {
    [pillar: string]: {
      month_year: string;
      avg_engagement: number;
      sum_engagement: number;
      post_count: number;
      platforms?: {
        [platform: string]: {
          avg_engagement: number;
          sum_engagement: number;
          post_count: number;
        }
      }
    }[];
  };
  brand_pillar_metrics: {
    [brand: string]: {
      [pillar: string]: {
        avg_engagement: number;
        sum_engagement: number;
        post_count: number;
        monthly_metrics?: {
          month_year: string;
          avg_engagement: number;
          sum_engagement: number;
          post_count: number;
        }[];
        platforms?: {
          [platform: string]: {
            avg_engagement: number;
            sum_engagement: number;
            post_count: number;
          }
        }
      }
    }
  };
  pillars: string[];
  platforms: string[];
}

interface PillarsPageDataParams {
  pillar?: string;
  startDate?: string | null;
  endDate?: string | null;
  orgId?: number;
  brand?: string; // Legacy single brand parameter
  brands?: string[]; // New multiple brands parameter for personal page
  is_personal_page?: string; // Flag to indicate personal page context
}

export function usePillarsPageData(params: PillarsPageDataParams = {}) {
  const axiosClient = useAxiosClient();
  const { orgId: orgIdFromParams } = useParams<{ orgId: string }>();
  
  // Use orgId from params if provided, otherwise use from route params
  const orgId = params.orgId || parseInt(orgIdFromParams || '0', 10);
  const { pillar, startDate, endDate, brand, brands, is_personal_page } = params;

  // Build the query parameters
  const queryParams = new URLSearchParams();
  if (orgId) queryParams.append('org', orgId.toString());
  if (pillar) queryParams.append('pillar', pillar);
  if (startDate) queryParams.append('start_date', startDate);
  if (endDate) queryParams.append('end_date', endDate);
  if (brand) queryParams.append('brand', brand); // Legacy single brand
  if (brands && brands.length > 0) {
    brands.forEach(b => queryParams.append('brands', b)); // Multiple brands
  }
  if (is_personal_page) queryParams.append('is_personal_page', is_personal_page);

  // React Query automatically caches by the key
  const {
    data,
    isLoading,
    error,
  } = useQuery(['pillarsPageData', orgId, pillar, startDate, endDate, brand, brands, is_personal_page], async () => {
    if (!orgId) {
      throw new Error('No organization selected');
    }

    const response = await axiosClient.get<PillarPageData>(
      `/api/get-pillars-page-data?${queryParams.toString()}`
    );

    // If the response has an 'error' field, throw an error
    if ('error' in response.data) {
      throw new Error(
        typeof response.data.error === 'string'
          ? response.data.error
          : 'An error occurred fetching pillar data'
      );
    }

    return response.data;
  }, {
    // Add a staleTime to prevent unnecessary refetches
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Disable automatic refetching when window is focused
    refetchOnWindowFocus: false,
    // Only retry once if there's an error
    retry: 1,
    // Handle null/undefined data
    select: (data) => data ?? null,
  });

  // Return in the same format as other hooks but include isLoading directly
  return {
    data: data ?? null,
    loading: isLoading,
    isLoading, // Include this for newer components using the isLoading property
    error: error ? (error as Error).message : null,
  };
} 